import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Virtual, Navigation, EffectFade, Autoplay } from "swiper/modules";
import StoryComponent from "@components/StoryComponent";
import StoryModalTemplate from "@components/StoryModalTemplate";
import PauseIcon from "./img/pause.svg";
import PlayIcon from "./img/play.svg";
import ExitIcon from "./img/exit.svg";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/virtual";

import "./style.scss";

import ArrowTip from "./img/arrow-tip-right.svg";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { motion, useAnimation } from "framer-motion";
import { filterUltraSpaceStories, getModuleValue, useWindowSize } from "../../Helpers";
import {
  STORY_CACHE_TIME,
  STORY_STALE_TIME,
  getStoryDescribe,
  getStoryList,
  markStoryAsSeen,
} from "../../api/story";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router";
import StoryPoints from "../StoryPoints";
import {
  DETAILS_CACHE_TIME,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "../../api/consumer";

export function StoryTimelineComponent({
  lenght,
  stopAnimation,
  onAnimationComplete,
  customTime = false,
  isAssetReady = false,
  currentIndexInGroup,
}) {
  const controls = useAnimation();

  function startAnimation() {
    controls.start({
      width: "100%",
      transition: {
        duration: customTime.current ? customTime.current : 15,
        type: "tween",
        ease: "linear",
      },
    });
  }

  useEffect(() => {
    if (isAssetReady) {
      startAnimation();
    }

    if (stopAnimation) {
      controls.stop();
    }
  }, [stopAnimation, isAssetReady, currentIndexInGroup]);

  return (
    <div className="story-group-timeline">
      {Array(lenght)
        .fill(null)
        .map((_, index) => (
          <div
            className={`${currentIndexInGroup > index ? "white" : ""}`}
            key={index}
          >
            {index === currentIndexInGroup && (
              <motion.div
                animate={controls}
                onAnimationComplete={() => onAnimationComplete()}
                className="progress-bar"
              />
            )}
          </div>
        ))}
    </div>
  );
}

export function StoryHeader({
  title,
  groupIndex,
  currentGroup,
  setPauseStory,
  pauseStory,
  closeStories,
  mobile = false,
}) {
  if (mobile) {
    return (
      <div className="story-header">
        <div className="copy">
          <h3 className="title">{title}</h3>
        </div>
        <div className="icons">
          <button onClick={() => setPauseStory((prev) => !prev)}>
            {pauseStory ? (
              <img src={PlayIcon} alt="włącz" />
            ) : (
              <img src={PauseIcon} alt="zatrzymaj" />
            )}
          </button>
          <button onClick={() => closeStories()}>
            <img src={ExitIcon} alt="zamknij" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="story-header">
      <div className="copy">
        <h3 className="title">{title}</h3>
      </div>
      {groupIndex === currentGroup && (
        <div className="icons">
          <button onClick={() => setPauseStory((prev) => !prev)}>
            {pauseStory ? (
              <img src={PlayIcon} alt="włącz" />
            ) : (
              <img src={PauseIcon} alt="zatrzymaj" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default NiceModal.create(() => {
  const modal = useModal();

  const swiperRef = useRef(null);

  const timeoutRef = useRef(null);
  const { width } = useWindowSize();
  const navigate = useNavigate();

  const [storyArray, setStoryArray] = useState([]);
  const [bonus, setBonus] = useState(0);

  const { data: stories, isLoading: isStoriesListLoading } = useQuery({
    queryKey: ["story-list", modal.args?.queryKey ?? "normal"],
    queryFn: () => getStoryList(modal.args?.payload),
    staleTime: STORY_STALE_TIME,
    cacheTime: STORY_CACHE_TIME,
    suspense: false,
  });

  const { data: userDetails, refetch: refetchUserDetails, isFetched: isDetailsFetched } = useQuery({
    queryKey: ["user-details"],
    queryFn: () => getUserDetails(),
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
    suspense: false,
  });

  const selectedStories = useRef([]);
  const hasLockedStories = useRef(false);

  if (
    !hasLockedStories.current &&
    isDetailsFetched &&
    Array.isArray(stories?.stories)
  ) {
    const visibleStoryGroups =
      filterUltraSpaceStories(
        stories.stories,
        userDetails?.consumer?.ultraInsider ?? false
      ) ?? [];

    selectedStories.current = modal.args?.singleStory
      ? visibleStoryGroups.filter(
          (item) => item.ident === modal.args?.singleStory
        )
      : visibleStoryGroups;
    hasLockedStories.current = true;
  }

  const initialStory = modal.args?.initialStoryUUID
    ? selectedStories.current?.findIndex(
        (e) => e.uuid === modal.args?.initialStoryUUID
      ) ?? 0
    : 0;

  const [isAssetReady, setIsAssetReady] = useState(false);

  const [currentIndexInGroup, setCurrentIndexInGroup] = useState(0);
  const [pauseStory, setPauseStory] = useState(false);

  const customTime = useRef(false);

  const queryClient = useQueryClient();

  const storyUUIDsByOrder = selectedStories.current.map((e) => e?.uuid);

  const isGoingBack = useRef(false);

  const {
    status,
    data,
    error,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = useInfiniteQuery({
    queryKey: ["stories", modal.args?.queryKey ?? "normal"],
    queryFn: async ({ pageParam }) => {
      if (pageParam === undefined || pageParam === "split-load") {
        return getStoryDescribe(pageParam, storyUUIDsByOrder, initialStory);
      }
      return getStoryDescribe(pageParam);
    },
    getNextPageParam: (firstPage) => {
      let index = storyUUIDsByOrder?.findIndex((e) => e === firstPage.uuid);

      if (Array.isArray(firstPage)) {
        const lastElement = firstPage[firstPage.length - 1]; //f Safari dont support .at()
        index = storyUUIDsByOrder?.findIndex((e) => e === lastElement.uuid);
      }

      if (index === -1 || index >= storyUUIDsByOrder.length) {
        return undefined;
      }
      return storyUUIDsByOrder[index + 1];
    },

    getPreviousPageParam: (lastPage) => {
      let index = storyUUIDsByOrder?.findIndex((e) => e === lastPage.uuid);

      if (Array.isArray(lastPage)) {
        const firstElement = lastPage[0]; //f Safari dont support .at()
        index = storyUUIDsByOrder?.findIndex((e) => e === firstElement.uuid);
      }

      if (index === -1 || index - 1 < 0) {
        return undefined;
      }
      return storyUUIDsByOrder[index - 1];
    },
    refetchOnMount: true,
    // staleTime: STORY_STALE_TIME,
    // cacheTime: STORY_CACHE_TIME,
  });

  let flatData = data?.pages.flat() ?? [];

  const initialStoryVirtualIndex = flatData.findIndex(
    (e) => e.uuid === storyUUIDsByOrder[initialStory]
  );

  const [currentGroup, setCurrentGroup] = useState(initialStoryVirtualIndex);

  // useEffect(() => {
  //   // cold load with some cache data, search in lastPage/firstPage for gaps
  //   if (initialStoryVirtualIndex === -1 && data?.pages.length !== 0 && !isFetching) {
  //     console.log("Hit")
  //     fetchNextPage({ pageParam: "split-load" })
  //   }
  // }, [])

  useEffect(() => {
    if (data?.pages) {
      setStoryArray(flatData.map((story) => story.stories));
    }
  }, [data?.pages]);

  useEffect(() => {
    if (isGoingBack.current) {
      swiperRef.current.swiper.slideTo(currentGroup + 1, 0, false);
      setCurrentGroup((prev) => prev + 1);
      isGoingBack.current = false;
    }
  }, [storyArray]);

  const groupsLength = storyUUIDsByOrder?.length ?? 0;
  const currentGroupLength = storyArray?.[currentGroup]?.length ?? 0;

  const storySeenMutation = useMutation({
    mutationFn: (uuid) => markStoryAsSeen(uuid),
    onSuccess: (seenResult) => {
      if (currentGroupLength - 1 === currentIndexInGroup) {
        // invalidate whole stories  on last story

        if (seenResult.awarded) {
          refetchUserDetails();
        }
        if (seenResult.bonus && seenResult.awarded) {
          setBonus(seenResult.bonus);
        }
        if (seenResult.awarded === false) {
          setBonus(0);
        }

        queryClient.invalidateQueries({
          queryKey: ["story-list", modal.args?.queryKey ?? "normal"],
          exact: true,
        });
      }
      queryClient.setQueryData(
        ["stories", modal.args?.queryKey ?? "normal"],
        (data) => {
          const newData = { ...data };
          if (!newData.pages.every((entry) => !Array.isArray(entry))) {
            newData.pages = newData.pages.flat();
          }
          if (seenResult.awarded) {
            newData.pages[currentGroup].rewarded =
              newData.pages[currentGroup].uuid;
          }
          newData.pages[currentGroup].stories[currentIndexInGroup].seen = true;

          return newData;
        }
      );
    },
  });

  useEffect(() => {
    if (storyArray?.[currentGroup]?.[currentIndexInGroup]?.uuid) {
      dataLayer.push({
        event: "view_stories",
        stories: [{ ...storyArray[currentGroup][currentIndexInGroup] }],
      });
    }
    if (
      storyArray.length !== 0 &&
      storyArray?.[currentGroup]?.[currentIndexInGroup]?.uuid
    ) {
      storySeenMutation.mutate(
        storyArray[currentGroup][currentIndexInGroup].uuid
      );
    }
  }, [currentGroup, currentIndexInGroup, storyArray.length]);

  function prevStoryInGroup(e) {
    e.stopPropagation();
    if (currentIndexInGroup === 0) {
      if (currentGroup === 0) {
        return;
      }
      swiperRef.current.swiper.slideTo(
        swiperRef.current.swiper.activeIndex - 1
      );
      setCurrentIndexInGroup(storyArray[currentGroup - 1]?.length - 1);
      setIsAssetReady(false);
      return;
    }

    setBonus(0);
    setCurrentIndexInGroup((prev) => --prev);
  }

  function nextStoryInGroup(e) {
    if (e) {
      e.stopPropagation();
    }
    if (currentIndexInGroup === currentGroupLength - 1) {
      if (flatData[currentGroup].uuid === storyUUIDsByOrder[groupsLength - 1]) {
        closeStories();
        return;
      }
      const firstNotSeenStory =
        flatData[currentGroup + 1]?.stories?.findIndex((e) => !e.seen) ?? -1;
      swiperRef.current.swiper.slideTo(
        swiperRef.current.swiper.activeIndex + 1
      );
      setCurrentIndexInGroup(firstNotSeenStory === -1 ? 0 : firstNotSeenStory);
      setIsAssetReady(false);
      return;
    }

    setBonus(0);
    setCurrentIndexInGroup((prev) => ++prev);
  }

  function stopStory(e) {
    timeoutRef.current = setTimeout(() => {
      setPauseStory(true);
    }, 300);
  }

  function resumeStory() {
    clearTimeout(timeoutRef.current);
    setPauseStory(false);
  }

  function closeStories() {
    setBonus(0);
    if (modal?.args?.closePath) {
      navigate(modal?.args?.closePath);
    }
    modal.hide();
    document
      .querySelector("body")
      .classList.remove("ReactModal__Body--open");
  }

  if (
    width >= 1020 &&
    storyArray.length !== 0 &&
    initialStoryVirtualIndex !== -1
  ) {
    return (
      <StoryModalTemplate
        isOpen={modal.visible}
        onClose={() => {
          closeStories();
        }}
        onAfterClose={() => modal.remove()}
      >
        <button
          onClick={prevStoryInGroup}
          className="swiper-button-prev stories"
        >
          <img src={ArrowTip} alt="prev-ArrowTip" />
        </button>
        <button
          onClick={nextStoryInGroup}
          className="swiper-button-next stories"
        >
          <img src={ArrowTip} alt="next-ArrowTip" />
        </button>

        <Swiper
          navigation={{
            nextEl: ".swiper-slide.swiper-slide-next",
            prevEl: ".swiper-slide.swiper-slide-prev",
          }}
          modules={[Virtual, Navigation]}
          initialSlide={initialStoryVirtualIndex}
          slidesPerView={"auto"}
          ref={swiperRef}
          onBeforeInit={() => {
            setCurrentGroup(initialStoryVirtualIndex);
          }}
          onSlidePrevTransitionStart={(index) => {
            isGoingBack.current = true;
            const firstNotSeenStory =
              flatData[index.activeIndex]?.stories?.findIndex((e) => !e.seen) ??
              -1;
            setCurrentGroup(index.activeIndex);
            setCurrentIndexInGroup(
              firstNotSeenStory === -1 ? 0 : firstNotSeenStory
            );
            setBonus(0);
            fetchPreviousPage();
          }}
          onSlideNextTransitionStart={(index) => {
            const firstNotSeenStory =
              flatData[index.activeIndex]?.stories?.findIndex((e) => !e.seen) ??
              -1;
            setCurrentGroup(index.activeIndex);
            setCurrentIndexInGroup(
              firstNotSeenStory === -1 ? 0 : firstNotSeenStory
            );
            setBonus(0);
            fetchNextPage();
          }}
          longSwipes={false}
          className="main-story-swiper"
          centeredSlides={true}
          lazyPreloadPrevNext={1}
        >
          {storyArray?.map((storiesGroup, groupIndex) => (
            <SwiperSlide
              key={groupIndex}
              onClick={() => {
                if (currentGroup !== groupIndex) {
                  swiperRef.current.swiper.slideTo(
                    swiperRef.current.swiper.clickedIndex
                  );
                }
              }}
            >
              <div className="stories-group-wrapper">
                {flatData[currentGroup]?.award?.available &&
                  currentGroup === groupIndex && (
                    <StoryPoints
                      fill={
                        ((currentIndexInGroup + 1) / currentGroupLength) * 100
                      }
                      animate={
                        flatData[currentGroup].uuid ===
                          flatData[currentGroup]?.rewarded &&
                        flatData[currentGroup]?.storiesCount - 1 ===
                          currentIndexInGroup
                      }
                      bonus={bonus}
                    >
                      {flatData[currentGroup]?.award?.points}
                    </StoryPoints>
                  )}
                {storiesGroup?.map((e, index) => {
                  // initial story
                  const isCurrentStroy =
                    groupIndex === currentGroup &&
                    index === currentIndexInGroup;

                  return (
                    <div
                      className={`story-wrapper ${
                        groupIndex === currentGroup
                          ? index === currentIndexInGroup
                            ? "visible"
                            : ""
                          : index === 0
                          ? "visible"
                          : ""
                      }`}
                      key={index}
                    >
                      <StoryHeader
                        title={e.content.title}
                        groupIndex={groupIndex}
                        currentGroup={currentGroup}
                        setPauseStory={setPauseStory}
                        pauseStory={pauseStory}
                      />

                      <div className="story-contnet">
                        {currentGroup === groupIndex && (
                          <StoryTimelineComponent
                            lenght={currentGroupLength}
                            stopAnimation={pauseStory}
                            onAnimationComplete={() => {
                              if (currentIndexInGroup === index)
                                nextStoryInGroup();
                            }}
                            isAssetReady={isAssetReady}
                            customTime={customTime}
                            currentIndexInGroup={currentIndexInGroup}
                          />
                        )}

                        <StoryComponent
                          img={getModuleValue(
                            e.content.modules,
                            "hero_image",
                            "file.url"
                          )}
                          video={getModuleValue(
                            e.content.modules,
                            "video",
                            "file.url"
                          )}
                          currentIndexInGroup={
                            currentGroup === groupIndex
                              ? currentIndexInGroup
                              : 0 // initial slide
                          }
                          setIsAssetReady={setIsAssetReady}
                          isStoryPaused={pauseStory}
                          isCurrentGroup={groupIndex === currentGroup}
                          isCurrentStory={isCurrentStroy}
                          customTime={customTime}
                          buttons={e.content?.modules?.filter(
                            (e) => e.type === "button"
                          )}
                          closeStories={closeStories}
                        />

                        {isCurrentStroy && (
                          <div className="story-navigation">
                            <div
                              onClick={(e) => prevStoryInGroup(e)}
                              className="prev"
                            />
                            <div
                              onClick={(e) => nextStoryInGroup(e)}
                              className="next"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </StoryModalTemplate>
    );
  }

  if (storyArray.length !== 0 && initialStoryVirtualIndex !== -1) {
    return (
      <StoryModalTemplate
        isOpen={modal.visible}
        onClose={() => {
          closeStories();
        }}
        onAfterClose={() => modal.remove()}
      >
        <Swiper
          modules={[Virtual]}
          initialSlide={initialStoryVirtualIndex}
          slidesPerView={1}
          ref={swiperRef}
          onBeforeInit={() => {
            setCurrentGroup(initialStoryVirtualIndex);
          }}
          onSlidePrevTransitionStart={(index) => {
            isGoingBack.current = true;
            const firstNotSeenStory =
              flatData[index.activeIndex]?.stories?.findIndex((e) => !e.seen) ??
              -1;
            setCurrentGroup(index.activeIndex);
            setCurrentIndexInGroup(
              firstNotSeenStory === -1 ? 0 : firstNotSeenStory
            );
            setBonus(0);
            fetchPreviousPage();
          }}
          onSlideNextTransitionStart={(index) => {
            const firstNotSeenStory =
              flatData[index.activeIndex]?.stories?.findIndex((e) => !e.seen) ??
              -1;
            setCurrentGroup(index.activeIndex);
            setCurrentIndexInGroup(
              firstNotSeenStory === -1 ? 0 : firstNotSeenStory
            );
            setBonus(0);
            fetchNextPage();
          }}
          longSwipes={false}
          className="main-story-swiper"
          lazyPreloadPrevNext={1}
          virtual
        >
          {storyArray?.map((storiesGroup, groupIndex) => (
            <SwiperSlide key={groupIndex}>
              <div className="stories-group-wrapper">
                {flatData[currentGroup]?.award?.available &&
                  currentGroup === groupIndex && (
                    <StoryPoints
                      fill={
                        ((currentIndexInGroup + 1) / currentGroupLength) * 100
                      }
                      animate={
                        flatData[currentGroup].uuid ===
                          flatData[currentGroup]?.rewarded &&
                        flatData[currentGroup]?.storiesCount - 1 ===
                          currentIndexInGroup
                      }
                      bonus={bonus}
                    >
                      {flatData[currentGroup]?.award?.points}
                    </StoryPoints>
                  )}
                {storiesGroup?.map((e, index) => (
                  <div
                    className={`story-wrapper ${
                      index === currentIndexInGroup ? "visible" : ""
                    }`}
                    key={index}
                  >
                    <StoryHeader
                      title={e.content.title}
                      groupIndex={groupIndex}
                      currentGroup={currentGroup}
                      setPauseStory={setPauseStory}
                      pauseStory={pauseStory}
                      closeStories={closeStories}
                      mobile={true}
                    />

                    <div
                      className="story-contnet"
                      onPointerDownCapture={stopStory}
                      onPointerUpCapture={resumeStory}
                    >
                      {currentGroup === groupIndex && (
                        <StoryTimelineComponent
                          lenght={currentGroupLength}
                          stopAnimation={pauseStory}
                          onAnimationComplete={() => {
                            if (currentIndexInGroup === index)
                              nextStoryInGroup();
                          }}
                          isAssetReady={isAssetReady}
                          customTime={customTime}
                          currentIndexInGroup={currentIndexInGroup}
                        />
                      )}

                      <StoryComponent
                        img={getModuleValue(
                          e.content.modules,
                          "hero_image",
                          "file.url"
                        )}
                        video={getModuleValue(
                          e.content.modules,
                          "video",
                          "file.url"
                        )}
                        currentIndexInGroup={
                          currentGroup === groupIndex ? currentIndexInGroup : 0 // initial slide
                        }
                        setIsAssetReady={setIsAssetReady}
                        isStoryPaused={pauseStory}
                        isCurrentStory={
                          groupIndex === currentGroup &&
                          index === currentIndexInGroup
                        }
                        isCurrentGroup={groupIndex === currentGroup}
                        customTime={customTime}
                        buttons={e.content?.modules?.filter(
                          (e) => e.type === "button"
                        )}
                        closeStories={closeStories}
                      />

                      <div className="story-navigation">
                        <div
                          onClick={(e) => prevStoryInGroup(e)}
                          className="prev"
                        />
                        <div
                          onClick={(e) => nextStoryInGroup(e)}
                          className="next"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </StoryModalTemplate>
    );
  }
});
